import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Main App Component
 * Feature: 001-github-notifications
 */
import { useState, useCallback, useEffect } from 'react';
import { Tabs } from './components/Tabs';
import { Profile } from './components/Profile';
import { OAuthCallback } from './components/OAuthCallback';
import { Notifications } from './components/Notifications';
import { Settings } from './components/Settings';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useWebSocket } from './hooks/useWebSocket';
import { useNotifications } from './hooks/useNotifications';
function App() {
    const [activeTab, setActiveTab] = useState('profile');
    const [view, setView] = useState('main');
    const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('sessionToken'));
    // Check if we're on the OAuth callback route
    useEffect(() => {
        if (window.location.pathname === '/auth/callback') {
            setView('oauth-callback');
        }
    }, []);
    // Notification state
    const { notifications, total, isLoading: notificationsLoading, isSyncing, syncError, sync, handleWebSocketMessage, } = useNotifications({
        enabled: isAuthenticated && activeTab === 'notifications',
    });
    // WebSocket connection
    const { status: wsStatus } = useWebSocket({
        enabled: isAuthenticated,
        onMessage: handleWebSocketMessage,
    });
    const handleOAuthSuccess = useCallback(() => {
        setIsAuthenticated(true);
        setView('main');
        setActiveTab('profile');
        window.history.replaceState({}, '', '/');
    }, []);
    const handleOAuthError = useCallback((error) => {
        console.error('OAuth error:', error);
        setView('main');
        window.history.replaceState({}, '', '/');
    }, []);
    const handleLogin = useCallback(() => {
        // Handled by Profile component's OAuth button
    }, []);
    // OAuth callback view
    if (view === 'oauth-callback') {
        return (_jsx("div", { className: "min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center", children: _jsx("div", { className: "bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full", children: _jsx(OAuthCallback, { onSuccess: handleOAuthSuccess, onError: handleOAuthError }) }) }));
    }
    const tabs = [
        { id: 'profile', label: 'Profile' },
        { id: 'notifications', label: 'Notifications' },
        { id: 'settings', label: 'Settings' },
    ];
    return (_jsx("div", { className: "min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800", children: _jsx("div", { className: "container mx-auto px-4 py-8", children: _jsxs("div", { className: "max-w-6xl mx-auto", children: [_jsxs("header", { className: "mb-8", children: [_jsx("h1", { className: "text-4xl font-bold text-center mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400", children: "GitHub Notifications Manager" }), _jsx("p", { className: "text-center text-gray-600 dark:text-gray-400", children: "Monitor and manage your GitHub notifications in real-time" })] }), _jsxs("div", { className: "bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden", children: [isAuthenticated && (_jsx("div", { className: "px-6 pt-4", children: _jsx(Tabs, { tabs: tabs, activeTab: activeTab, onTabChange: setActiveTab }) })), _jsx("div", { className: "p-6", children: _jsxs(ErrorBoundary, { children: [(!isAuthenticated || activeTab === 'profile') && (_jsx(Profile, { isAuthenticated: isAuthenticated, onLogin: handleLogin })), isAuthenticated && activeTab === 'notifications' && (_jsx(Notifications, { notifications: notifications, total: total, isLoading: notificationsLoading, isSyncing: isSyncing, syncError: syncError, onSync: sync })), isAuthenticated && activeTab === 'settings' && _jsx(Settings, {})] }) })] }), _jsx("footer", { className: "mt-8 flex items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400", children: isAuthenticated && _jsx(ConnectionStatusIndicator, { status: wsStatus }) })] }) }) }));
}
function ConnectionStatusIndicator({ status }) {
    const styles = {
        connected: { dot: 'bg-green-500', text: 'Connected' },
        connecting: { dot: 'bg-yellow-500 animate-pulse', text: 'Connecting...' },
        reconnecting: { dot: 'bg-yellow-500 animate-pulse', text: 'Reconnecting...' },
        disconnected: { dot: 'bg-gray-400', text: 'Disconnected' },
    };
    const { dot, text } = styles[status];
    return (_jsxs("span", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: `w-2 h-2 rounded-full ${dot}` }), text] }));
}
export default App;
